import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Document, pdfjs } from "react-pdf";
import { type PDFDocumentProxy, type PDFPageProxy } from "pdfjs-dist";

import { faro } from "@grafana/faro-web-sdk";
import { DefaultErrorPanel } from "@vega-core/components/Cards/DefaultErrorPanel/DefaultErrorPanel";
import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { Panel } from "@vega-core/components/Layout/Panel";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { SidebarModal } from "@vega-core/components/Modals/SidebarModal/SidebarModal";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { getBrowserSupportInfo } from "@vega-core/utils/browserSupport";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const options = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

function reportPdfError(
  stage: "pdf.load" | "pdf.source",
  error: Error,
  documentId: string | undefined,
): void {
  faro.api.pushError(error, {
    type: stage,
    context: {
      documentId: documentId ?? "",
      sessionId: faro.api.getSession()?.id ?? "unknown",
      pathname: window.location.pathname,
    },
  });
}

import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { useIsMountedRef } from "@vega-core/hooks/useIsMountedRef";

import { LazyPDFPage } from "./LazyPDFPage";
import { PDFViewerMiniMap } from "./PDFViewerMiniMap";
import { type PdfCustomTextRenderer, PDFViewerPage } from "./PDFViewerPage";
import { PDFViewerPanel } from "./PDFViewerPanel";
import { usePdfViewerResize } from "./usePdfViewerResize";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

const PDF_VIEWER_INITIAL_STATE = {
  scale: 1,
  mobileScale: 0.6,
  currentDocument: "",
  currentDocumentId: "",
  currentLinkId: "",
  currentDocumentPage: 1,
  totalDocumentPages: 0,
};

const PDF_VIEWER_LOCALSTORAGE_KEY = "pdf_viewer_current_page";

type Props = {
  blobOrString?: Blob | string;
  children?: ReactNode;
  isLoading?: boolean;
  isOpen?: boolean;
  initialScale?: number;
  setInitialScale?: Dispatch<SetStateAction<number>>;
  initialPageNumber?: number;
  // When navigationKey changes, always navigate to initialPageNumber even if it's the same value
  navigationKey?: number;
  setIsOpen?: (isOpen: boolean) => void;
  documentOptions?: OptionType[];
  previewDocumentId?: string;
  setPreviewDocumentId?: (option: string | undefined) => void;
  isDownloadable?: boolean;
  isDownloadEnabled?: boolean;
  isDownloading?: boolean;
  isPageMapVisible?: boolean;
  isScrollable?: boolean;
  showDocumentDropdown?: boolean;
  headerActions?: ReactNode;
  onDownload?: (documentId: string) => Promise<void>;
  onInitialScrollComplete?: () => void;
  embedded?: boolean;
  // Used when there is only one document to display and we want to use a custom file name for the download
  downloadFileName?: string;
  // Controls the overflow behavior of the scroll container. 'scroll' always shows scrollbar, 'auto' only when needed
  overflowBehavior?: "auto" | "scroll";
  // Controls whether PDF annotations (notes, comments, links) are rendered. Defaults to true
  renderAnnotationLayer?: boolean;
  customTextRenderer?: PdfCustomTextRenderer;
};

export const PDFViewer = ({
  blobOrString,
  children,
  initialScale,
  setInitialScale,
  initialPageNumber,
  navigationKey,
  isLoading = false,
  isOpen = true,
  documentOptions = [],
  setPreviewDocumentId = () => {},
  previewDocumentId,
  setIsOpen,
  isDownloadable,
  isDownloadEnabled = true,
  isDownloading = false,
  isPageMapVisible: isPageMapVisibleInitial = true,
  isScrollable = true,
  showDocumentDropdown = true,
  headerActions,
  onDownload,
  onInitialScrollComplete,
  embedded = false,
  downloadFileName,
  overflowBehavior = "scroll",
  renderAnnotationLayer = true,
  customTextRenderer,
}: Props) => {
  const isMobile = !useDeviceWidth("md");
  const isPageMapVisible = isMobile ? false : isPageMapVisibleInitial;
  const [isDocumentLoading, setIsDocumentLoading] = useState(true);
  const isBrowserSupported = getBrowserSupportInfo().supported;
  const unsupportedBrowserError = (
    <DefaultErrorPanel
      description={m.pdfViewer_unsupportedBrowserDescription()}
      title={m.pdfViewer_unsupportedBrowserTitle()}
    />
  );

  const getInitialScale = () => {
    if (initialScale) return initialScale;

    if (isMobile) return PDF_VIEWER_INITIAL_STATE.mobileScale;

    return PDF_VIEWER_INITIAL_STATE.scale;
  };

  const [scale, setScale] = useState(getInitialScale());
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [currentDocumentPage, setCurrentDocumentPage] = useState(
    PDF_VIEWER_INITIAL_STATE.currentDocumentPage,
  );
  const [initializedDocumentId, setInitializedDocumentId] = useState<
    string | null
  >(null);
  const [hasScrolledToInitialPage, setHasScrolledToInitialPage] =
    useState(false);
  const [totalDocumentPages, setTotalDocumentPages] = useState(
    PDF_VIEWER_INITIAL_STATE.totalDocumentPages,
  );
  const renderedPagesRef = useRef<Set<number>>(new Set());
  const [firstPageRendered, setFirstPageRendered] = useState(false);
  const lastExternalPageNumberRef = useRef<number | undefined>(undefined);
  const lastNavigationKeyRef = useRef<number | undefined>(undefined);
  const [documentKey, setDocumentKey] = useState(
    () => previewDocumentId || `doc-${crypto.randomUUID()}`,
  );
  const prevBlobRef = useRef<Blob | string | undefined>(blobOrString);
  const stableFileRef = useRef<Blob | string | undefined>(blobOrString);
  const stableFileDocIdRef = useRef<string | undefined>(previewDocumentId);

  if (
    previewDocumentId &&
    (previewDocumentId !== stableFileDocIdRef.current || !stableFileRef.current)
  ) {
    stableFileRef.current = blobOrString;
    stableFileDocIdRef.current = previewDocumentId;
  } else if (!previewDocumentId) {
    stableFileRef.current = blobOrString;
  }

  const stableFile = stableFileRef.current;

  const [pageWidth, setPageWidth] = useState<number | undefined>(undefined);
  const [pageHeight, setPageHeight] = useState<number | undefined>(undefined);

  const { containerRef, zoomLevels } = usePdfViewerResize(
    windowDimensions,
    setWindowDimensions,
    setScale,
    previewDocumentId,
    totalDocumentPages,
    pageWidth,
  );

  const scrollToPage = (pageNumber: number) => {
    if (pageNumber < 1 || pageNumber > totalDocumentPages) return;
    const container = containerRef.current;

    if (!container) return;
    const pageElement = container.querySelector(
      `[data-page-number="${pageNumber}"]`,
    ) as HTMLElement | null;

    if (pageElement) {
      const containerRect = container.getBoundingClientRect();
      const pageRect = pageElement.getBoundingClientRect();
      container.scrollTop += pageRect.top - containerRect.top;
    }
  };

  const currentDocument =
    documentOptions.length > 0
      ? documentOptions.find((option) => option.value === previewDocumentId)
          ?.value
      : previewDocumentId;

  const isMountedRef = useIsMountedRef();

  const onDocumentLoadSuccess = (pdf: PDFDocumentProxy): void => {
    setTotalDocumentPages(pdf.numPages);
    setIsDocumentLoading(false);
  };

  useLayoutEffect(() => {
    if (
      !isOpen ||
      isDocumentLoading ||
      hasScrolledToInitialPage ||
      totalDocumentPages === 0 ||
      !firstPageRendered
    ) {
      return;
    }
    scrollToPage(currentDocumentPage);
    setHasScrolledToInitialPage(true);
    onInitialScrollComplete?.();
  }, [
    isOpen,
    isDocumentLoading,
    hasScrolledToInitialPage,
    currentDocumentPage,
    firstPageRendered,
    totalDocumentPages,
    scrollToPage,
    onInitialScrollComplete,
  ]);

  // Track document changes and update key
  useEffect(() => {
    if (previewDocumentId) {
      if (previewDocumentId !== documentKey) {
        setDocumentKey(previewDocumentId);
      }

      return;
    }

    if (blobOrString !== prevBlobRef.current) {
      setDocumentKey(`doc-${crypto.randomUUID()}`);
      prevBlobRef.current = blobOrString;
    }
  }, [previewDocumentId, blobOrString, documentKey]);

  useEffect(() => {
    setIsDocumentLoading(true);
    setHasScrolledToInitialPage(false);
    setPageWidth(undefined);
    setPageHeight(undefined);
    setTotalDocumentPages(0);
    setCurrentDocumentPage(1);
    setFirstPageRendered(false);
    renderedPagesRef.current.clear();
    requestAnimationFrame(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    });
  }, [documentKey]);

  // Sets initial page number
  useEffect(() => {
    if (!previewDocumentId || initializedDocumentId === previewDocumentId)
      return;

    let pageToSet: number | null = null;

    if (initialPageNumber) {
      pageToSet = initialPageNumber;
    } else {
      const savedPage = localStorage.getItem(
        `${PDF_VIEWER_LOCALSTORAGE_KEY}-${previewDocumentId}`,
      );

      if (savedPage) {
        const parsed = parseInt(savedPage, 10);

        if (!isNaN(parsed)) pageToSet = parsed;
      }
    }

    setCurrentDocumentPage(
      pageToSet ?? PDF_VIEWER_INITIAL_STATE.currentDocumentPage,
    );
    setInitializedDocumentId(previewDocumentId);
  }, [initialPageNumber, previewDocumentId, initializedDocumentId]);

  // Sets initial scale when scale prop changes or document changes
  useEffect(() => {
    if (initialScale !== undefined) {
      setScale(initialScale);
    }
  }, [initialScale]);

  // Reset scale when document changes
  useEffect(() => {
    if (previewDocumentId) {
      setScale(getInitialScale());
    }
  }, [previewDocumentId]);

  // Handle external page number changes (e.g., from comment navigation in embedded mode)
  useEffect(() => {
    // Only process if initialPageNumber is provided
    if (!initialPageNumber) return;

    // Check if this is a new navigation request
    const isPageChanged =
      initialPageNumber !== lastExternalPageNumberRef.current;
    const isNavigationKeyChanged =
      navigationKey !== undefined &&
      navigationKey !== lastNavigationKeyRef.current;

    // Skip if neither page nor navigationKey has changed
    if (!isPageChanged && !isNavigationKeyChanged) return;

    // Wait for document to load before processing page changes
    if (totalDocumentPages === 0) return;

    // Update refs to track this navigation
    lastExternalPageNumberRef.current = initialPageNumber;

    if (navigationKey !== undefined) {
      lastNavigationKeyRef.current = navigationKey;
    }

    // Update internal page state
    setCurrentDocumentPage(initialPageNumber);

    if (isScrollable) {
      scrollToPage(initialPageNumber);
    }
  }, [initialPageNumber, navigationKey]);

  // Saves the current page number to local storage
  useEffect(() => {
    if (!previewDocumentId) return;
    localStorage.setItem(
      `${PDF_VIEWER_LOCALSTORAGE_KEY}-${previewDocumentId}`,
      currentDocumentPage.toString(),
    );
  }, [currentDocumentPage, previewDocumentId]);

  const handlePageRenderSuccess = (pageIndex: number) => {
    renderedPagesRef.current.add(pageIndex);

    if (!firstPageRendered) setFirstPageRendered(true);
  };

  const handlePageLoadSuccess = (page: PDFPageProxy) => {
    if (!isMountedRef.current || pageWidth !== undefined) return;
    const viewport = page.getViewport({ scale: 1 });
    setPageWidth(viewport.width);
    setPageHeight(viewport.height);
  };

  const getDocument = () => {
    if (!isBrowserSupported) return unsupportedBrowserError;

    if (!isScrollable) {
      return (
        <Document
          className={cnTwMerge("w-fit m-auto border-2", {
            "rounded-lg border-border/primary": previewDocumentId,
          })}
          error={<DefaultErrorPanel />}
          file={stableFile}
          key={documentKey}
          loading={<LoadingSpinner className="absolute bg-transparent" />}
          noData={<Panel description={m.common_pleaseSelectAFile()} />}
          onLoadError={(error) =>
            reportPdfError("pdf.load", error, previewDocumentId)
          }
          onLoadSuccess={onDocumentLoadSuccess}
          onSourceError={(error) =>
            reportPdfError("pdf.source", error, previewDocumentId)
          }
          options={options}
        >
          <PDFViewerPage
            className="relative border-2 rounded-lg border-transparent"
            customTextRenderer={customTextRenderer}
            onLoadSuccess={handlePageLoadSuccess}
            pageIndex={currentDocumentPage - 1}
            pageNumber={currentDocumentPage}
            renderAnnotationLayer={renderAnnotationLayer}
            scale={scale}
          >
            {children}
          </PDFViewerPage>
        </Document>
      );
    }

    return null;
  };

  const currentDocumentName =
    (documentOptions.find((option) => option.value === previewDocumentId)
      ?.label ||
      downloadFileName) ??
    "document";

  const renderScrollableContent = () => {
    if (isLoading || isDownloading) {
      return <LoadingSpinner className="absolute bg-transparent" />;
    }

    if (!isBrowserSupported) return unsupportedBrowserError;

    return (
      <Document
        className={
          embedded ? "flex-1 min-h-0 flex flex-col overflow-hidden" : undefined
        }
        error={<DefaultErrorPanel />}
        file={stableFile}
        key={documentKey}
        loading={<LoadingSpinner className="absolute bg-transparent" />}
        noData={<Panel description={m.common_pleaseSelectAFile()} />}
        onLoadError={(error) =>
          reportPdfError("pdf.load", error, previewDocumentId)
        }
        onLoadSuccess={onDocumentLoadSuccess}
        onSourceError={(error) =>
          reportPdfError("pdf.source", error, previewDocumentId)
        }
        options={options}
      >
        <div
          className={cnTwMerge(
            "flex flex-col md:flex-row relative",
            embedded && !isPageMapVisible && "flex-1 min-h-0 overflow-hidden",
          )}
        >
          <div
            className={cnTwMerge(
              "md:flex-1 px-4 py-6 overscroll-contain",
              overflowBehavior === "auto" ? "overflow-auto" : "overflow-scroll",
              embedded && !isPageMapVisible
                ? "flex-1 min-h-0"
                : {
                    "md:h-[calc(100dvh-164px)]": true,
                    "h-[calc(100dvh-374px)]": isPageMapVisible,
                    "h-[calc(100dvh-184px)]":
                      !isPageMapVisible || totalDocumentPages === 1,
                  },
            )}
            ref={containerRef}
          >
            <div
              className={cnTwMerge({
                "skeleton-box w-full": isDocumentLoading,
              })}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {!isDocumentLoading &&
                Array.from({ length: totalDocumentPages }, (_, index) => (
                  <LazyPDFPage
                    isDocumentReady
                    key={`${documentKey}-${index}`}
                    onVisible={setCurrentDocumentPage}
                    pageHeight={pageHeight}
                    pageNumber={index + 1}
                    scale={scale}
                  >
                    <PDFViewerPage
                      className="relative border-2 rounded-lg border-border/primary overflow-hidden"
                      customTextRenderer={customTextRenderer}
                      onLoadSuccess={handlePageLoadSuccess}
                      onRenderSuccess={() => handlePageRenderSuccess(index)}
                      pageIndex={index}
                      pageNumber={index + 1}
                      renderAnnotationLayer={renderAnnotationLayer}
                      resetKey={documentKey}
                      scale={scale}
                    >
                      {children}
                    </PDFViewerPage>
                  </LazyPDFPage>
                ))}
            </div>
          </div>
          <PDFViewerMiniMap
            currentDocumentPage={currentDocumentPage}
            isDownloading={isDownloading}
            isLoading={isLoading}
            isPageMapVisible={isPageMapVisible}
            previewDocumentId={previewDocumentId || ""}
            scrollToPage={scrollToPage}
            setCurrentDocumentPage={setCurrentDocumentPage}
            totalDocumentPages={totalDocumentPages}
            windowDimensions={windowDimensions}
          />
        </div>
      </Document>
    );
  };

  const renderContent = () => (
    <>
      <PDFViewerPanel
        blobOrString={blobOrString}
        currentDocument={currentDocument}
        currentDocumentName={currentDocumentName}
        currentDocumentPage={currentDocumentPage}
        documentsDropdown={
          showDocumentDropdown &&
          isTruthy(documentOptions.length) &&
          isTruthy(setPreviewDocumentId) ? (
            <ThemedDropdown
              isClearable={false}
              name="document-dropdown"
              onChange={(option) => setPreviewDocumentId(option?.value)}
              options={documentOptions}
              value={currentDocument}
            />
          ) : null
        }
        {...(embedded && { embedded })}
        headerActions={headerActions}
        isDownloadEnabled={isDownloadEnabled}
        isDownloadable={isDownloadable}
        isLoading={Boolean(isLoading)}
        isScrollable={isScrollable}
        onDownload={onDownload}
        scale={scale}
        scrollToPage={scrollToPage}
        setCurrentDocumentPage={setCurrentDocumentPage}
        setIsOpen={setIsOpen}
        setScale={setInitialScale ?? setScale}
        totalDocumentPages={totalDocumentPages}
        zoomLevels={zoomLevels}
      />
      {isScrollable ? (
        renderScrollableContent()
      ) : (
        <div
          className={cnTwMerge(
            "flex flex-col md:flex-row relative",
            embedded && !isPageMapVisible && "flex-1 min-h-0 overflow-hidden",
          )}
        >
          <div
            className={cnTwMerge(
              "md:flex-1 px-4 py-6 overscroll-contain",
              overflowBehavior === "auto" ? "overflow-auto" : "overflow-scroll",
              embedded && !isPageMapVisible
                ? "flex-1 min-h-0"
                : {
                    "md:h-[calc(100dvh-164px)]": true,
                    "h-[calc(100dvh-374px)]": isPageMapVisible,
                    "h-[calc(100dvh-184px)]":
                      !isPageMapVisible || totalDocumentPages === 1,
                  },
            )}
            ref={containerRef}
          >
            {isLoading || isDownloading ? (
              <LoadingSpinner className="absolute bg-transparent" />
            ) : (
              getDocument()
            )}
          </div>
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <div
        className={cnTwMerge(
          "border-1 rounded-lg border-border/primary min-h-[50dvh]",
          !isPageMapVisible && "h-full flex flex-col",
        )}
      >
        {renderContent()}
      </div>
    );
  }

  return (
    <SidebarModal
      customWidthClassnames="w-full md:w-2/3 xl:w-1/2 overflow-hidden"
      dataTestId="pdf-viewer"
      hideCloseButton
      isOpen={isOpen}
      keepPreviousState={false}
      noPadding
      noScrolling
      setIsOpen={setIsOpen ?? (() => {})}
    >
      {renderContent()}
    </SidebarModal>
  );
};
